/**
 * Neutral shapes for a grammar course, whatever language it teaches.
 *
 * Grammatik and Gramática store their content under different field names —
 * `prompt_de` against `prompt_pt`, `case_sensitive` against `accent_sensitive`,
 * examples keyed by `de` against `pt`. Those names are right where they are:
 * they say what the field holds in a file a person authors by hand.
 *
 * They are wrong in a component. A card that renders `payload.prompt_de` can
 * only ever serve one course, so every fix to it lands once and survives once.
 * These types are the shape the components see; each app supplies a small
 * adapter from its own payload, and the adapter is the only place the field
 * names appear.
 *
 * WHY AN ADAPTER RATHER THAN CHANGING WHAT IS STORED
 *
 * Storing the neutral shape directly would be tidier and was the first plan. It
 * would also mean rewriting fifty already-seeded German lessons to gain
 * something the adapter gives for forty lines, on an app that is in daily use.
 * The adapter is reversible; a data migration is not.
 */

/** Text in the two languages a course explains itself in, keyed by ISO code. */
export type AuxText = Record<string, string>;

type ExerciseBase = {
  id: string;
  /**
   * Compare strictly.
   *
   * `case_sensitive` in German, `accent_sensitive` in Portuguese. Both mean the
   * same thing — this exercise is about the very thing the comparison would
   * otherwise forgive — so the components see one flag and each course decides
   * what it forgives.
   */
  strict: boolean;
};

export type GrammarExercise =
  | (ExerciseBase & {
      kind: 'fill_blank';
      /** The target-language sentence, containing exactly one blank marker. */
      prompt: string;
      answer: string;
      accept: string[];
      hint: AuxText | null;
    })
  | (ExerciseBase & {
      kind: 'choice';
      prompt: string | null;
      instruction: AuxText | null;
      options: string[];
      answer: string;
    })
  | (ExerciseBase & {
      kind: 'transform';
      given: string;
      instruction: AuxText;
      answer: string;
      accept: string[];
    })
  | (ExerciseBase & {
      kind: 'order';
      chunks: string[];
      answer: string[];
      also_accept: string[][];
    });

/** A [start, end) range into an example's target-language text. */
export type FocusSpan = [number, number];

export type GrammarLessonView = {
  n: number;
  slug: string;
  block: number;
  /** The lesson's name, in the language being taught. */
  title: string;
  rule: AuxText;
  anchor: string;
  parallel: { target: string; aux: AuxText } | null;
  /**
   * "Your other language made you do this; this one does not."
   *
   * Only Gramática carries these today. The component renders one when it is
   * there and nothing when it is not, so the German course needs no adapter
   * change to gain them later.
   */
  relief: AuxText | null;
  trap: AuxText | null;
  table: { headers: string[]; rows: string[][] } | null;
  examples: { target: string; focus: FocusSpan[]; aux: AuxText }[];
};

/**
 * Everything the shared components need to know about one course.
 *
 * The answer comparators are passed in rather than imported because they are
 * the one piece that genuinely differs: German transliterates its umlauts and
 * never folds them, Portuguese folds its accents by default. See each course's
 * `answer.ts` for why.
 */
export type GrammarCourse = {
  appId: string;
  /**
   * The languages explanations appear in, in the order they read best for this
   * learner. The user's own primary language wins when it is one of these.
   */
  aux: readonly [string, string];
  /** Human-readable name for an anchor value, e.g. 'like German'. */
  anchorLabel(anchor: string): string;
  /** Tailwind classes for the anchor badge. `neither` should read loudest. */
  anchorStyle(anchor: string): string;
  blockName(block: number): string;
  isAnswerCorrect(input: string, accepted: string[], strict: boolean): boolean;
  isOrderCorrect(input: string[], accepted: string[][]): boolean;
};

/**
 * One lesson's record, as `GET /:course/scores` returns it.
 *
 * Four measures rather than one, because a single accuracy is misleading here
 * and the reason is not obvious. See the route for the full argument; the short
 * version is that lifetime accuracy is dominated by the day the rule was new,
 * and that an accuracy without `exercises_seen / exercises_total` beside it is
 * a claim about a sample nobody chose.
 */
export type GrammarLessonScore = {
  lesson_n: number;
  exercises_total: number;
  exercises_seen: number;
  struggling: number;
  archived: number;
  attempts: number;
  correct: number;
  recent_attempts: number;
  recent_correct: number;
  first_pass_attempts: number;
  first_pass_correct: number;
  last_attempt_at: string | null;
};

export type GrammarScores = {
  /** How many answers back "recent" reaches. */
  recent_window: number;
  lessons: GrammarLessonScore[];
};

/**
 * How well a lesson landed, said by the learner rather than measured.
 *
 * `current` is the most recent rating and `first` the earliest, so a row can
 * show "2★ → 4★" — the improvement the accuracy numbers are trying to describe,
 * reported by the only witness who was in the room.
 */
export type GrammarRating = {
  lesson_n: number;
  /** 1 = did not land, 5 = got it. */
  current: number;
  first: number;
  times_rated: number;
  history: { stars: number; rated_on: string }[];
};

/** A saved lesson, with the learner's own reason for saving it. */
export type GrammarBookmark = {
  lesson_n: number;
  note: string | null;
  created_at: string;
};

/** The blank marker in a `fill_blank` prompt. Matches both authoring schemas. */
export const BLANK = '___';

/** Every accepted free-text answer for one exercise. */
export function acceptedAnswers(exercise: GrammarExercise): string[] {
  switch (exercise.kind) {
    case 'fill_blank':
    case 'transform':
      return [exercise.answer, ...exercise.accept];
    case 'choice':
      return [exercise.answer];
    case 'order':
      return [exercise.answer.join(' ')];
  }
}

/** The canonical correct answer, for the card back and the miss list. */
export function correctAnswerText(exercise: GrammarExercise): string {
  return exercise.kind === 'order' ? exercise.answer.join(' ') : exercise.answer;
}

/**
 * The two explanations, in reading order, skipping any the content lacks.
 *
 * The old per-app components printed `text.en` and nothing else, so a
 * Portuguese-speaking learner of German read every hint in her second-best
 * language and a German learner of Portuguese would have read hers in her
 * third. Ordering by the user's own primary and falling back to whatever the
 * text actually has fixes both, and is the reason this is one function rather
 * than a `.en` in six components.
 */
export function readingOrder(
  text: AuxText | null | undefined,
  course: GrammarCourse,
  primaryLang?: string,
): string[] {
  if (!text) return [];
  const ordered =
    primaryLang && course.aux.includes(primaryLang)
      ? [primaryLang, ...course.aux.filter((lang) => lang !== primaryLang)]
      : [...course.aux];
  const out = ordered.map((lang) => text[lang]).filter((value): value is string => Boolean(value));
  // A course whose aux languages disagree with the stored keys would render
  // nothing at all. Showing what is there beats showing an empty box.
  return out.length > 0 ? out : Object.values(text);
}
